import { useEffect, useRef, useState } from "react";
import { CommandCenterIcon } from "./CommandCenterIcon";
import { doublePFieldEligible, isDoublePCandidate, shouldSubmit } from "./commandCenterHubModel";
import type {
  CommandCenterHubAction,
  CommandCenterHubProps,
  HubRoute,
} from "./CommandCenterHub.types";
import "./command-center-hub.css";

export type { CommandCenterHubAction } from "./CommandCenterHub.types";

// The Final Hub Center — the approved Command Center body.
//
// Presentation only: it renders the SQ monogram + live clock, icon-first Quick
// Access + Divisions, and a neon command dock with a six-tool canvas. It never
// fetches or performs a provider action — every real action goes back through
// onNavigate / onCommand / onTool, which the container wires to the repository.
//
// SSR-safe: all window / DOM / timer access lives inside effects and event
// handlers (the command-center tests render this with renderToString).

const TOOLS: { id: CommandCenterHubAction; label: string; accent: string }[] = [
  { id: "video", label: "Video", accent: "#ff5db1" },
  { id: "camera", label: "Camera", accent: "#38b6ff" },
  { id: "files", label: "Files", accent: "#ffb62e" },
  { id: "photo", label: "Photo", accent: "#57ef85" },
  { id: "code", label: "Code", accent: "#9d6bff" },
  { id: "other", label: "Other", accent: "#35e0d4" },
];

const DOUBLE_P_WINDOW_MS = 300;

function Clock() {
  const [time, setTime] = useState("");
  useEffect(() => {
    const tick = () =>
      setTime(
        new Intl.DateTimeFormat(undefined, {
          hour: "2-digit",
          minute: "2-digit",
          second: "2-digit",
          hour12: false,
        }).format(new Date()),
      );
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, []);
  return (
    <div className="sq-clock" role="timer" aria-label={`Current time ${time}`}>
      {time}
    </div>
  );
}

function Brand() {
  return (
    <>
      <div className="sq-mark" role="img" aria-label="Square Code">
        <svg viewBox="0 0 260 120">
          <defs>
            <linearGradient id="hub-sq-gradient" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0" stopColor="#37e0ff" />
              <stop offset=".5" stopColor="#9d6bff" />
              <stop offset="1" stopColor="#ff5db1" />
            </linearGradient>
            <filter id="hub-sq-glow" x="-60%" y="-60%" width="220%" height="220%">
              <feGaussianBlur stdDeviation="4.2" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>
          <text
            x="130"
            y="88"
            textAnchor="middle"
            fill="none"
            stroke="url(#hub-sq-gradient)"
            strokeWidth="2.2"
            fontSize="90"
            fontWeight="800"
            letterSpacing="10"
            filter="url(#hub-sq-glow)"
          >
            SQ
          </text>
        </svg>
      </div>
      <Clock />
    </>
  );
}

function Tile({ item, onActivate }: { item: HubRoute; onActivate: (item: HubRoute) => void }) {
  const blocked = Boolean(item.availability && item.availability !== "ready");
  const detail =
    item.count === null || item.count === undefined
      ? item.description
      : `${item.count} active`;
  return (
    <button
      type="button"
      className="hub-tile"
      style={{ "--hub-accent": item.accent } as React.CSSProperties}
      onClick={() => onActivate(item)}
      aria-label={`${item.label}. ${blocked ? item.reason ?? "Unavailable" : item.description}`}
      aria-disabled={blocked || undefined}
      data-state={item.availability ?? "ready"}
    >
      <span className="hub-icon">
        <CommandCenterIcon name={item.icon} />
      </span>
      <span className="hub-tip">
        <b>{item.label}</b>
        <span>{blocked ? item.reason ?? "Unavailable" : detail}</span>
      </span>
    </button>
  );
}

export function CommandCenterHub({
  quickAccess,
  divisions,
  onNavigate,
  onCommand,
  onTool,
  commandPlaceholder = "Type a command",
  className = "",
}: CommandCenterHubProps) {
  const [command, setCommand] = useState("");
  const [canvasOpen, setCanvasOpen] = useState(false);
  const [dispatching, setDispatching] = useState(false);
  const [message, setMessage] = useState("");
  const inputRef = useRef<HTMLTextAreaElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const firstPAt = useRef<number | null>(null);
  const firstPTimer = useRef<number | null>(null);
  const composing = useRef(false);

  const closeCanvas = (restore = true) => {
    setCanvasOpen(false);
    if (restore) requestAnimationFrame(() => inputRef.current?.focus());
  };

  useEffect(() => {
    const outside = (event: MouseEvent) => {
      if (
        canvasOpen &&
        !popRef.current?.contains(event.target as Node) &&
        !(event.target as Element).closest?.("[data-tool-trigger]")
      ) {
        closeCanvas(false);
      }
    };
    document.addEventListener("mousedown", outside);
    return () => document.removeEventListener("mousedown", outside);
  }, [canvasOpen]);

  useEffect(
    () => () => {
      if (firstPTimer.current) window.clearTimeout(firstPTimer.current);
    },
    [],
  );

  const navigate = (item: HubRoute) => {
    if (item.availability && item.availability !== "ready") {
      setMessage(item.reason ?? `${item.label} is unavailable.`);
      return;
    }
    onNavigate(item.path, item.id);
  };

  const submit = async () => {
    const value = command.trim();
    if (!value || dispatching) return;
    setDispatching(true);
    setMessage("");
    const result = await onCommand(value).catch(() => ({
      ok: false,
      reason: "Command dispatch failed.",
    }));
    setDispatching(false);
    if (result.ok) {
      setCommand("");
      setMessage(result.reason ?? "Command sent.");
    } else {
      setMessage(result.reason ?? "Command could not be sent.");
    }
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (shouldSubmit(event, composing.current, canvasOpen)) {
      event.preventDefault();
      void submit();
      return;
    }
    // Shift+Enter falls through to the textarea's native newline.
    if (event.key === "Escape" && canvasOpen) {
      event.preventDefault();
      closeCanvas();
      return;
    }
    const now = performance.now();
    const armed = firstPAt.current !== null && now - firstPAt.current <= DOUBLE_P_WINDOW_MS;
    // The first `p` is not prevented, so on the second `p` keydown the field
    // already holds that `p`. A field holding only the armed `p`/`P` stays
    // eligible so the double-tap can open the canvas (see doublePFieldEligible).
    const fieldEligible = doublePFieldEligible(command.trim(), armed);
    if (isDoublePCandidate(event, fieldEligible, canvasOpen, composing.current)) {
      if (armed) {
        event.preventDefault();
        firstPAt.current = null;
        if (firstPTimer.current) window.clearTimeout(firstPTimer.current);
        setCommand(""); // discard the inserted first `p`
        setCanvasOpen(true);
        return;
      }
      firstPAt.current = now;
      firstPTimer.current = window.setTimeout(() => {
        firstPAt.current = null;
      }, DOUBLE_P_WINDOW_MS);
    } else if (event.key.length === 1) {
      firstPAt.current = null;
    }
  };

  const chooseTool = async (action: CommandCenterHubAction) => {
    setMessage("");
    const result = await onTool(action).catch(() => ({
      ok: false,
      reason: "Tool action failed.",
    }));
    if (result.ok) closeCanvas();
    else setMessage(result.reason ?? "Tool is unavailable.");
  };

  return (
    <div className={`command-center-page ${className}`.trim()}>
      <header className="hub-top">
        <Brand />
      </header>
      <main className="hub-stage">
        <section aria-labelledby="hub-quick">
          <h2 id="hub-quick" className="hub-section-label">
            Quick access
          </h2>
          <div className="hub-quick">
            {quickAccess.map((item) => (
              <Tile key={item.id} item={item} onActivate={navigate} />
            ))}
          </div>
        </section>
        <section aria-labelledby="hub-divisions">
          <h2 id="hub-divisions" className="hub-section-label">
            Divisions
          </h2>
          <div className="hub-divisions">
            {divisions.map((item) => (
              <Tile key={item.id} item={item} onActivate={navigate} />
            ))}
          </div>
        </section>
      </main>
      <footer className="hub-dock">
        <div
          ref={popRef}
          id="hub-tool-canvas"
          className={`hub-tool-canvas ${canvasOpen ? "open" : ""}`.trim()}
          role="menu"
          aria-label="Command tools"
          aria-hidden={!canvasOpen}
        >
          <div className="hub-tool-heading">Attach</div>
          <div className="hub-tool-grid">
            {TOOLS.map((tool) => (
              <button
                key={tool.id}
                type="button"
                role="menuitem"
                className="hub-tool"
                style={{ "--hub-accent": tool.accent } as React.CSSProperties}
                onClick={() => void chooseTool(tool.id)}
              >
                <CommandCenterIcon name={tool.id} />
                <span>{tool.label}</span>
              </button>
            ))}
          </div>
        </div>
        <div className={`hub-dock-field ${dispatching ? "busy" : ""}`.trim()}>
          <button
            type="button"
            data-tool-trigger
            className={`hub-tool-trigger ${canvasOpen ? "open" : ""}`.trim()}
            onClick={() => setCanvasOpen((open) => !open)}
            aria-label="Open command tools"
            aria-controls="hub-tool-canvas"
            aria-expanded={canvasOpen}
          >
            <CommandCenterIcon name="terminal" />
          </button>
          <textarea
            ref={inputRef}
            rows={1}
            className="hub-command-input"
            value={command}
            onChange={(event) => setCommand(event.target.value)}
            onKeyDown={handleKeyDown}
            onCompositionStart={() => {
              composing.current = true;
            }}
            onCompositionEnd={() => {
              composing.current = false;
            }}
            placeholder={commandPlaceholder}
            aria-label="Command input"
            disabled={dispatching}
          />
          <span className="hub-command-hint">
            {dispatching ? "sending" : "Enter to send, pp to attach"}
          </span>
        </div>
        <div className="hub-status" role="status" aria-live="polite">
          {message}
        </div>
      </footer>
    </div>
  );
}
